/**
 * 저장된 COMPOSED 문서가 **어느 위험도 평가 방식으로 조립됐는가** — 편집 로드 prefill 요청에 실어
 * 보낼 `findingRiskMethod` 값을 문서(템플릿 스냅샷 + content)에서 해소한다.
 *
 * 왜 필요한가: 편집 로드는 콤보 후보·기준표 rows를 얻으려고 서버 prefill을 다시 부르는데, 방식을
 * 안 실으면 서버가 **현장 세트**(예: 3x3 빈도·강도)로 resolve한다. 그 응답의 기준표 rows
 * (`risk-level-criteria-rows` 등)가 resolvedRows에 주입되고, rc `resolveDynamicSelectRows`는
 * resolvedRows를 content(박제 행)보다 **우선**하므로 3단계(THREE_LEVEL) 문서를 열면 「위험성 수준」
 * select가 현장 세트 기준으로 옵션 0·현재 값 미표시가 됐다(2026-09-14 위험성평가 결과서 실측).
 * 신규 작성 경로(P4e 재resolve)는 이미 `findingRiskMethod`를 싣는다 — 편집 로드만 빠져 있었다.
 *
 * 출처 우선순위(셋 다 문서가 조립될 때 남긴 흔적이고, 정상 문서에서는 같은 값이다):
 *  ① 템플릿 카탈로그 파라미터 지정 — rc `resolveFindingRiskMethodValue`(서버 해소 규칙의 거울).
 *     `{from:'variable'}`이면 작성자가 고른 변수 값이라 가장 직접적인 근거다.
 *  ② content `__catalogParams['risk-assessment-config'].effective.method` — compose가 규약으로
 *     남기는 「실제 낙점한 방식」(지정이 없어 계층 폴백으로 정해진 경우도 여기 남는다).
 *  ③ content `riskAssessmentMethodType` — 서버 `injectRiskConfigChannels`가 branch condition용으로
 *     주입하는 flat 키(resolved config의 methodType).
 * 셋 다 없으면 undefined — 호출부는 종전대로 방식 없이 prefill한다(현장 세트 = 문서 방식인 문서,
 * 위험도 축이 없는 DLR·SI·TBM은 여기로 떨어지거나 값이 같아 결과가 종전과 동일하다).
 *
 * 페이지에서 떼어낸 이유: 단독으로 검사하기 위해서다. 페이지 spec은 rc를 통째로 mock하므로
 * ①이 늘 undefined라 ②③만 도달한다 — 세 출처의 순서는 여기 spec이 잰다.
 */
import { resolveFindingRiskMethodValue } from '@safetyhub/report-composer';
// 카탈로그 키·방식 flat 키는 shared 정본(서버 `injectRiskConfigChannels`·rc와 같은 상수).
import {
  CATALOG_PARAM_RESOLUTION_KEY,
  RISK_METHOD_CATALOG_KEY,
  RISK_METHOD_FLAT_KEY,
} from '@safetyhub/shared';
import type { CatalogParamResolutionMap, ReportTemplate } from '@safetyhub/shared';

const asValue = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw !== '' ? raw : undefined;

export function resolveStoredFindingRiskMethod(
  template: ReportTemplate | null | undefined,
  content: Record<string, unknown>,
): string | undefined {
  // ① 템플릿 지정(literal / variable→content 값)
  const fromBinding = resolveFindingRiskMethodValue(template, content);
  if (fromBinding !== undefined) return fromBinding;

  // ② compose가 남긴 해소 박제
  const resolutions = content[CATALOG_PARAM_RESOLUTION_KEY];
  if (resolutions && typeof resolutions === 'object' && !Array.isArray(resolutions)) {
    const effective = (resolutions as CatalogParamResolutionMap)[RISK_METHOD_CATALOG_KEY]?.effective;
    const fromEffective = asValue(effective?.method);
    if (fromEffective !== undefined) return fromEffective;
  }

  // ③ 서버 주입 flat 키
  return asValue(content[RISK_METHOD_FLAT_KEY]);
}
